import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Shirt, Moon, Droplets, CheckCircle2, Download, Clock, ShieldCheck, Star, XIcon, LockIcon } from 'lucide-react';
import { PromoBanner } from './components/PromoBanner';
import { RecentPurchaseToast } from './components/RecentPurchaseToast';
import { SocialTrustEngine } from './components/SocialTrustEngine';
import { PsychologicalPricing } from './components/PsychologicalPricing';
import { TwoPathsClose } from './components/TwoPathsClose';
import { FaqAccordion } from './components/FaqAccordion';
import { FadeInUp } from './components/FadeInUp';
const PRIVACY_CONTENT = `
Política de Privacidad — Vitarutinas

En Vitarutinas, protegemos tus datos. La información recolectada en este sitio se utiliza exclusivamente para la entrega del acceso a tu producto digital y para enviarte actualizaciones relevantes sobre tu protocolo.

No compartimos tus datos con terceros.

Puedes solicitar la eliminación de tu información en cualquier momento escribiendo a nuestro soporte.
`.trim();
const TERMS_CONTENT = `
Términos y Condiciones — Vitarutinas

Al adquirir el Protocolo Vitarutinas 7D, aceptas que se trata de un contenido de acceso digital inmediato.

Debido a la naturaleza del producto (descarga electrónica), una vez entregado el acceso, no se realizan devoluciones a menos que se aplique la garantía de satisfacción de 7 días descrita en esta página.

El precio de $4.990 CLP es un pago único, sin cargos recurrentes ni suscripciones ocultas.
`.trim();
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
export function App() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    // Preconnect to critical origins for faster resource loading
    const origins = ['https://cdn.magicpatterns.com', 'https://connect.facebook.net'];
    origins.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
    // Meta Pixel initialization (immediate — no delay to preserve ad metrics)
    if (typeof window.fbq === 'function') return;
    const n: any = window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(t);
    window.fbq('init', '1666572947695847');
    window.fbq('track', 'PageView');
  }, []);
  const handleAddToCart = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'AddToCart', {
        content_name: 'Método Anti-Inflamatorio 7D',
        content_type: 'product',
        value: 6.36,
        currency: 'USD'
      });
    }
  };
  return <div className="min-h-screen bg-cream font-sans text-charcoal selection:bg-gold selection:text-white pt-[68px]" data-id="element-0">
      <PromoBanner data-id="element-1" />
      <RecentPurchaseToast data-id="element-2" />
      {/* 1. HERO SECTION */}
      <section className="pt-4 pb-12 px-5 md:pt-16 md:pb-28 max-w-5xl mx-auto text-center" data-id="element-3">
        <FadeInUp data-id="element-4">
          <span className="inline-block py-1 px-3 rounded-full bg-sage text-white font-semibold text-xs tracking-wide mb-4 shadow-sm" data-id="element-5">
            Para la mujer que vive en CHILE
          </span>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-[#1D2E28] max-w-4xl mx-auto" data-id="element-6">
            ¿Hinchada como globo al final del día? Recupera tu abdomen ligero en
            48 horas
          </h1>
          <p className="text-base md:text-lg text-charcoal/80 mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-7">
            Sin dietas imposibles ni pasar hambre. El Método Vitarutinas 7D es
            el mapa exacto para resetear tu sistema digestivo con ingredientes
            que ya tienes en tu cocina
          </p>

          <div className="flex gap-2 mb-5 items-center justify-center" data-id="element-8">
            <div className="flex text-gold" data-id="element-9">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-10" />)}
            </div>
            <span className="text-charcoal/60 font-medium text-xs" data-id="element-11">
              +1,200 mujeres ya la usan
            </span>
          </div>

          <a href="#offer" onClick={e => {
          e.preventDefault();
          document.getElementById('offer')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="shimmer-btn w-full md:w-auto font-bold text-base md:text-xl py-4 px-8 md:px-12 rounded-2xl shadow-xl transition-all transform hover:scale-105 flex items-center justify-center mx-auto group mb-2 no-underline text-white bg-[#E67E22] hover:bg-[#D35400]" data-id="element-12">
            <span data-id="element-13">¡Sí, Quiero Desinflamar mi Abdomen!</span>
            <span className="mx-1.5" data-id="element-14">·</span>
            <span data-id="element-15">$4.990</span>
            <ArrowRight className="ml-2 w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform shrink-0" data-id="element-16" />
          </a>

          <div className="flex items-center justify-center gap-1.5 mb-10" data-id="element-17">
            <LockIcon className="w-3.5 h-3.5 text-charcoal/50" data-id="element-18" />
            <span className="text-xs md:text-sm text-charcoal/60 font-semibold tracking-wide uppercase" data-id="element-19">
              Pago único — Sin suscripciones
            </span>
          </div>
        </FadeInUp>

        <FadeInUp delay={0.2} data-id="element-20">
          <div className="relative mx-auto max-w-3xl" data-id="element-21">
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cream to-transparent z-10" data-id="element-22"></div>
            <img src="https://cdn.magicpatterns.com/uploads/jhsX1s3aT7AZa4ouMRvghZ/producto.jpg" alt="Protocolo 7 Días — Acceso completo en tablet y celular" width={1024} height={1024} fetchPriority="high" decoding="async" className="w-full h-auto rounded-3xl shadow-2xl object-cover border-4 border-white" data-id="element-23" />
          </div>
        </FadeInUp>
      </section>

      {/* 2. AGITACIÓN */}
      <section className="py-20 px-6 bg-white" data-id="element-24">
        <div className="max-w-5xl mx-auto" data-id="element-25">
          <FadeInUp data-id="element-26">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-16 text-center text-deep-green" data-id="element-27">
              Si esto te pasa, no estás sola
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-28">
            <FadeInUp delay={0.1} data-id="element-29">
              <div className="bg-cream p-8 rounded-3xl h-full border border-black/5 hover:shadow-md transition-shadow" data-id="element-30">
                <p className="text-charcoal/80 leading-relaxed text-lg" data-id="element-31">
                  🎈 Efecto Globo: Te despiertas plana y terminas el día con el
                  pantalón apretado.
                </p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-32">
              <div className="bg-cream p-8 rounded-3xl h-full border border-black/5 hover:shadow-md transition-shadow" data-id="element-33">
                <p className="text-charcoal/80 leading-relaxed text-lg" data-id="element-34">
                  😴 Pesadez Crónica: Sientes que tu digestión es lenta y te
                  quita la energía.
                </p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-35">
              <div className="bg-cream p-8 rounded-3xl h-full border border-black/5 hover:shadow-md transition-shadow" data-id="element-36">
                <p className="text-charcoal/80 leading-relaxed text-lg" data-id="element-37">
                  👗 Ropa Prohibida: Dejaste de usar vestidos ajustados por
                  miedo a cómo se ve tu abdomen.
                </p>
              </div>
            </FadeInUp>
          </div>

          <FadeInUp delay={0.4} data-id="element-38">
            <div className="max-w-3xl mx-auto text-center" data-id="element-39">
              <p className="text-xl md:text-2xl font-serif italic text-deep-green/90 leading-relaxed" data-id="element-40">
                "La buena noticia es que esa hinchazón no tiene que ser tu
                normal. Tu cuerpo puede sentirse más ligero cuando le das lo que
                realmente necesita — y es más simple de lo que crees."
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 3. PRESENTACIÓN DEL MECANISMO */}
      <section className="py-20 px-6 bg-cream" data-id="element-41">
        <div className="max-w-4xl mx-auto" data-id="element-42">
          <FadeInUp data-id="element-43">
            <div className="text-center mb-16" data-id="element-44">
              <span className="inline-block py-1 px-4 rounded-full bg-sage/20 text-sage font-bold text-sm tracking-wider uppercase mb-6" data-id="element-45">
                Qué incluye
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-deep-green" data-id="element-46">
                Un plan de 7 días diseñado
                <br data-id="element-47" />
                para tu ritmo de vida real
              </h2>
              <p className="text-lg md:text-xl text-charcoal/70 max-w-2xl mx-auto" data-id="element-48">
                No es grasa, es inflamación. Nuestro sistema de 50 páginas te
                enseña la ciencia de la desinflamación rápida. Es una guía paso
                a paso con todo lo que necesitas para empezar hoy — pensada para
                la mujer que trabaja, cocina rápido y no tiene tiempo de sobra.
              </p>
            </div>
          </FadeInUp>

          <div className="space-y-6" data-id="element-49">
            <FadeInUp delay={0.1} data-id="element-50">
              <div className="flex flex-col md:flex-row items-start md:items-center p-6 md:p-8 bg-white rounded-3xl shadow-sm border border-black/5" data-id="element-51">
                <div className="bg-sage/10 text-sage p-4 rounded-full mb-4 md:mb-0 md:mr-6 shrink-0" data-id="element-52">
                  <CheckCircle2 className="w-8 h-8" data-id="element-53" />
                </div>
                <div data-id="element-54">
                  <h3 className="font-bold text-xl md:text-2xl mb-2 text-deep-green" data-id="element-55">
                    Plan de comidas de 7 días
                  </h3>
                  <p className="text-charcoal/70 text-lg" data-id="element-56">
                    Recetas simples con ingredientes que encuentras en Jumbo,
                    Lider o Ferias. Rápidas de preparar, pensadas para reducir
                    la hinchazón.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-57">
              <div className="flex flex-col md:flex-row items-start md:items-center p-6 md:p-8 bg-white rounded-3xl shadow-sm border border-black/5" data-id="element-58">
                <div className="bg-sage/10 text-sage p-4 rounded-full mb-4 md:mb-0 md:mr-6 shrink-0" data-id="element-59">
                  <CheckCircle2 className="w-8 h-8" data-id="element-60" />
                </div>
                <div data-id="element-61">
                  <h3 className="font-bold text-xl md:text-2xl mb-2 text-deep-green" data-id="element-62">
                    Plan de acción día por día (Día 1 al 7)
                  </h3>
                  <p className="text-charcoal/70 text-lg" data-id="element-63">
                    Tés e infusiones específicas que apoyan tu digestión y
                    ayudan a que tu cuerpo se sienta menos pesado durante el
                    día.
                  </p>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-64">
              <div className="flex flex-col md:flex-row items-start md:items-center p-6 md:p-8 bg-white rounded-3xl shadow-sm border border-black/5" data-id="element-65">
                <div className="bg-sage/10 text-sage p-4 rounded-full mb-4 md:mb-0 md:mr-6 shrink-0" data-id="element-66">
                  <CheckCircle2 className="w-8 h-8" data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <h3 className="font-bold text-xl md:text-2xl mb-2 text-deep-green" data-id="element-69">
                    Recetario de Alivio Inmediato
                  </h3>
                  <p className="text-charcoal/70 text-lg" data-id="element-70">
                    Pequeñas rutinas que puedes hacer en cualquier momento para
                    ayudar a tu cuerpo a manejar mejor el estrés y la tensión
                    diaria.
                  </p>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 4. TESTIMONIOS DE TRANSFORMACIÓN */}
      <section className="py-20 px-6 bg-white" data-id="element-71">
        <div className="max-w-6xl mx-auto" data-id="element-72">
          <FadeInUp data-id="element-73">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-16 text-center text-deep-green max-w-2xl mx-auto" data-id="element-74">
              Mujeres reales, experiencias reales
            </h2>
          </FadeInUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-75">
            <FadeInUp delay={0.1} data-id="element-76">
              <div className="bg-cream p-8 rounded-3xl shadow-sm border border-black/5 h-full flex flex-col" data-id="element-77">
                <div className="flex text-gold mb-6" data-id="element-78">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-79" />)}
                </div>
                <p className="text-charcoal/80 italic leading-relaxed mb-8 flex-grow" data-id="element-80">
                  "Trabajo doble turno limpiando casas y llegaba a mi casa tan
                  hinchada que me cambiaba a pants apenas entraba. Pensé que era
                  normal por mi edad. Empecé la guía un lunes y para el jueves
                  ya sentía la diferencia. Nada complicado, todo con cosas que
                  ya tenía en la cocina."
                </p>
                <div className="flex items-center justify-between border-t border-black/5 pt-6" data-id="element-81">
                  <div data-id="element-82">
                    <p className="font-bold text-deep-green" data-id="element-83">María T.</p>
                    <p className="text-sm text-charcoal/50" data-id="element-84">La Florida</p>
                  </div>
                  <span className="bg-gold/10 text-gold font-bold py-1 px-3 rounded-full text-sm" data-id="element-85">
                    Verificada
                  </span>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2} data-id="element-86">
              <div className="bg-cream p-8 rounded-3xl shadow-sm border border-black/5 h-full flex flex-col" data-id="element-87">
                <div className="flex text-gold mb-6" data-id="element-88">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-89" />)}
                </div>
                <p className="text-charcoal/80 italic leading-relaxed mb-8 flex-grow" data-id="element-90">
                  "Yo ya había probado jugos verdes, detox, de todo. Nada me
                  duraba más de 3 días. Lo que me gustó de esto es que no te
                  pide cosas raras — son comidas normales y unos tés que de
                  verdad se sienten bien. Al quinto día mi esposo me dijo que se
                  me veía el abdomen más plano."
                </p>
                <div className="flex items-center justify-between border-t border-black/5 pt-6" data-id="element-91">
                  <div data-id="element-92">
                    <p className="font-bold text-deep-green" data-id="element-93">Elena R.</p>
                    <p className="text-sm text-charcoal/50" data-id="element-94">Las Condes</p>
                  </div>
                  <span className="bg-gold/10 text-gold font-bold py-1 px-3 rounded-full text-sm" data-id="element-95">
                    Verificada
                  </span>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.3} data-id="element-96">
              <div className="bg-cream p-8 rounded-3xl shadow-sm border border-black/5 h-full flex flex-col" data-id="element-97">
                <div className="flex text-gold mb-6" data-id="element-98">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-99" />)}
                </div>
                <p className="text-charcoal/80 italic leading-relaxed mb-8 flex-grow" data-id="element-100">
                  "Soy mamá de 3 y no tengo tiempo para nada elaborado. Esto lo
                  pude seguir sin cambiar mi rutina. Lo que más me sorprendió es
                  que dormí mejor desde el segundo día. Ya no me acuesto con esa
                  sensación de pesadez que me tenía harta. Valió completamente
                  la pena."
                </p>
                <div className="flex items-center justify-between border-t border-black/5 pt-6" data-id="element-101">
                  <div data-id="element-102">
                    <p className="font-bold text-deep-green" data-id="element-103">Carmen S.</p>
                    <p className="text-sm text-charcoal/50" data-id="element-104">Temuco</p>
                  </div>
                  <span className="bg-gold/10 text-gold font-bold py-1 px-3 rounded-full text-sm" data-id="element-105">
                    Verificada
                  </span>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      <SocialTrustEngine data-id="element-106" />
      <PsychologicalPricing data-id="element-107" />

      {/* 5. OFERTA Y PRECIO */}
      <section id="offer" className="py-20 px-6 bg-cream" data-id="element-108">
        <div className="max-w-3xl mx-auto" data-id="element-109">
          <FadeInUp data-id="element-110">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl border-2 border-dashed border-gold/50 relative text-center" data-id="element-111">
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-deep-green text-white py-2 px-6 rounded-full font-bold text-sm tracking-wider shadow-md" data-id="element-112">
                OFERTA DE LANZAMIENTO
              </div>

              <h3 className="font-serif text-2xl md:text-3xl font-bold text-deep-green mt-6 mb-8" data-id="element-113">
                Todo lo que recibes hoy:
              </h3>

              <div className="text-left max-w-md mx-auto space-y-4 mb-10" data-id="element-114">
                <div className="flex items-start gap-3" data-id="element-115">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-116" />
                  <div data-id="element-117">
                    <span className="font-bold text-charcoal" data-id="element-118">
                      Protocolo Maestro "Cero Hinchazón" (50 Páginas)
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-119">
                      — valor $14.990
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-120">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-121" />
                  <div data-id="element-122">
                    <span className="font-bold text-charcoal" data-id="element-123">
                      Sistema 3-3-3: El Secreto de las Infusiones
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-124">
                      — valor $9.990
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-125">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-126" />
                  <div data-id="element-127">
                    <span className="font-bold text-charcoal" data-id="element-128">
                      Activador Linfático Express (5 min)
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-129">
                      — valor $7.990
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-130">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 shrink-0" data-id="element-131" />
                  <div data-id="element-132">
                    <span className="font-bold text-charcoal" data-id="element-133">
                      Mapa de Compras Inteligente (Líder/Jumbo/Feria)
                    </span>
                    <span className="text-charcoal/50 text-sm ml-2" data-id="element-134">
                      — valor $4.990
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-4" data-id="element-135">
                <span className="text-charcoal/40 text-lg md:text-xl line-through font-medium" data-id="element-136">
                  Valor total: $37.960
                </span>
              </div>

              <div className="mb-2" data-id="element-137">
                <span className="text-sage font-bold text-base tracking-widest uppercase" data-id="element-138">
                  HOY LO LLEVAS TODO POR:
                </span>
              </div>

              <div className="mb-10" data-id="element-139">
                <span className="font-serif text-7xl md:text-8xl font-bold text-gold drop-shadow-sm" data-id="element-140">
                  $4.990
                </span>
              </div>

              <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-8" data-id="element-141">
                <div className="flex items-center bg-cream px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-charcoal/80" data-id="element-142">
                  <Download className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2 text-sage shrink-0" data-id="element-143" />{' '}
                  Descarga Inmediata
                </div>
                <div className="flex items-center bg-cream px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-charcoal/80" data-id="element-144">
                  <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2 text-sage shrink-0" data-id="element-145" />{' '}
                  Precio de Lanzamiento
                </div>
                <div className="flex items-center bg-cream px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-charcoal/80" data-id="element-146">
                  <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2 text-sage shrink-0" data-id="element-147" />{' '}
                  Garantía 7 Días
                </div>
              </div>

              <motion.a href="https://www.vitarutinas.com/cart/50330442137846:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="shimmer-btn w-full font-bold text-xl py-6 px-8 rounded-2xl shadow-xl flex items-center justify-center group mb-6 no-underline text-white bg-[#E67E22] hover:bg-[#D35400]" animate={{
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} whileHover={{
              scale: 1.07
            }} whileTap={{
              scale: 0.97
            }} data-id="element-148">
                Quiero mi Sistema 7D ahora
              </motion.a>

              <div className="flex flex-col items-center justify-center gap-4" data-id="element-149">
                <span className="text-charcoal/60 font-medium flex items-center text-[12px]" data-id="element-150">
                  Pago único · Sin suscripciones · Acceso de por vida
                </span>
                <div className="flex flex-wrap gap-3 items-center justify-center" data-id="element-151">
                  {/* Visa */}
                  <div className="bg-white rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-152">
                    <svg viewBox="0 0 750 471" className="h-5" fill="none" data-id="element-153">
                      <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763H278.198z" fill="#1A1F71" data-id="element-154" />
                      <path d="M524.307 142.687c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.634 28.141 26.485 43.845 46.754 53.185 20.77 9.536 27.77 15.636 27.77 24.17-.317 13.032-16.652 18.985-32.037 18.985-21.42 0-32.794-2.966-50.376-10.283l-6.902-3.113-7.519 43.845c12.507 5.467 35.638 10.199 59.673 10.454 56.062 0 92.425-26.187 92.862-66.884.317-22.27-14.016-39.215-44.8-53.185-18.652-9.072-30.078-15.099-30.078-24.267.317-8.258 9.703-16.834 30.712-16.834 17.567-.317 30.295 3.548 40.218 7.519l4.82 2.268 7.325-42.168z" fill="#1A1F71" data-id="element-155" />
                      <path d="M661.615 138.465h-41.231c-12.773 0-22.332 3.478-27.94 16.218l-79.245 179.545h56.062l11.19-29.189h68.397l6.508 29.189h49.474l-43.215-195.763zm-65.927 126.408l28.09-73.14 4.503-12.844 7.83 12.844 16.33 73.14h-56.753z" fill="#1A1F71" data-id="element-156" />
                      <path d="M232.903 138.465L180.663 271.96l-5.565-26.867c-9.703-31.107-39.93-64.921-73.744-81.822l47.822 170.64h56.379l83.826-195.446h-56.478z" fill="#1A1F71" data-id="element-157" />
                      <path d="M131.92 138.465H46.459l-.634 3.966c66.878 16.152 111.16 55.128 129.495 101.932L156.72 155.318c-3.331-12.423-12.907-16.535-24.8-16.853z" fill="#F9A533" data-id="element-158" />
                    </svg>
                  </div>
                  {/* Mastercard */}
                  <div className="bg-white rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-159">
                    <svg viewBox="0 0 152.407 108" className="h-5" fill="none" data-id="element-160">
                      <circle cx="60.412" cy="54" r="48" fill="#EB001B" data-id="element-161" />
                      <circle cx="91.995" cy="54" r="48" fill="#F79E1B" data-id="element-162" />
                      <path d="M76.204 18.631a47.834 47.834 0 0 0-15.792 35.369A47.834 47.834 0 0 0 76.204 89.37a47.834 47.834 0 0 0 15.791-35.37 47.834 47.834 0 0 0-15.791-35.369z" fill="#FF5F00" data-id="element-163" />
                    </svg>
                  </div>
                  {/* Mercado Pago */}
                  <div className="bg-[#00B1EA] rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-164">
                    <svg viewBox="0 0 80 20" className="h-3.5" fill="none" data-id="element-165">
                      <text x="40" y="14.5" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.3" data-id="element-166">
                        Mercado Pago
                      </text>
                    </svg>
                  </div>
                  {/* Redcompra */}
                  <div className="bg-[#E31E24] rounded-lg px-3 py-1.5 flex items-center justify-center shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-167">
                    <svg viewBox="0 0 80 20" className="h-3.5" fill="none" data-id="element-168">
                      <text x="40" y="14.5" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.3" data-id="element-169">
                        Redcompra
                      </text>
                    </svg>
                  </div>
                  {/* Transferencia Bancaria */}
                  <div className="bg-[#2D5F2D] rounded-lg px-3 py-1.5 flex items-center justify-center gap-1.5 shadow-[0_1px_4px_rgba(0,0,0,0.12)]" style={{
                  maxHeight: 28
                }} data-id="element-170">
                    <svg viewBox="0 0 20 20" className="h-3.5" fill="none" data-id="element-171">
                      <rect x="3" y="4" width="14" height="2" rx="0.5" fill="white" data-id="element-172" />
                      <rect x="5" y="7" width="2" height="7" fill="white" data-id="element-173" />
                      <rect x="9" y="7" width="2" height="7" fill="white" data-id="element-174" />
                      <rect x="13" y="7" width="2" height="7" fill="white" data-id="element-175" />
                      <rect x="3" y="15" width="14" height="2" rx="0.5" fill="white" data-id="element-176" />
                    </svg>
                    <svg viewBox="0 0 80 20" className="h-3" fill="none" data-id="element-177">
                      <text x="40" y="14" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="Arial, Helvetica, sans-serif" letterSpacing="0.2" data-id="element-178">
                        Transferencia
                      </text>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 6. GARANTÍA DE DESINFLAMACIÓN */}
      <section className="py-20 px-6 bg-deep-green text-cream text-center" data-id="element-179">
        <div className="max-w-3xl mx-auto" data-id="element-180">
          <FadeInUp data-id="element-181">
            <ShieldCheck className="w-20 h-20 text-gold mx-auto mb-8" data-id="element-182" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-gold" data-id="element-183">
              PRUÉBALO SIN RIESGO
            </h2>
            <p className="text-lg md:text-xl leading-relaxed mb-8 text-cream/90" data-id="element-184">
              Tienes{' '}
              <strong className="text-gold font-bold" data-id="element-185">7 días completos</strong>{' '}
              para probar el método. Si sientes que no fue útil para ti, solo
              escríbenos y te devolvemos el 100% de tu dinero. Sin preguntas,
              sin formularios complicados. Así de simple.
            </p>
            <p className="text-gold font-bold tracking-widest uppercase text-lg" data-id="element-186">
              GARANTÍA DE SATISFACCIÓN DE 7 DÍAS
            </p>
          </FadeInUp>
        </div>
      </section>

      <TwoPathsClose data-id="element-187" />

      {/* 7. CTA FINAL */}
      <section id="cta-final" className="py-20 px-6 bg-cream text-center" data-id="element-188">
        <div className="max-w-3xl mx-auto" data-id="element-189">
          <FadeInUp data-id="element-190">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-deep-green" data-id="element-191">
              Hoy puede ser el día en que dejas de sentirte hinchada
            </h2>
            <p className="text-lg md:text-xl text-charcoal/70 mb-10 max-w-2xl mx-auto" data-id="element-192">
              No necesitas otra dieta complicada. Solo un plan simple, con pasos
              claros, que puedes empezar hoy mismo con lo que ya tienes en tu
              cocina. Por menos de lo que cuesta un café.
            </p>

            <motion.a href="https://www.vitarutinas.com/cart/50330442137846:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="shimmer-btn w-full md:w-auto font-bold text-lg md:text-xl py-6 px-8 md:px-14 rounded-2xl shadow-xl flex items-center justify-center mx-auto group mb-3 no-underline text-white bg-[#E67E22] hover:bg-[#D35400]" animate={{
            scale: [1, 1.04, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} whileHover={{
            scale: 1.07
          }} whileTap={{
            scale: 0.97
          }} data-id="element-193">
              ¡Empezar mi Transformación por $4.990!
            </motion.a>

            <div className="flex items-center justify-center gap-1.5" data-id="element-194">
              <LockIcon className="w-3.5 h-3.5 text-charcoal/50" data-id="element-195" />
              <span className="text-xs md:text-sm text-charcoal/60 font-semibold tracking-wide uppercase" data-id="element-196">
                Pago único — Sin suscripciones
              </span>
            </div>
          </FadeInUp>
        </div>
      </section>

      <FaqAccordion data-id="element-197" />

      {/* 8. FOOTER */}
      <footer className="py-12 bg-white border-t border-black/5" data-id="element-198">
        <div className="max-w-4xl mx-auto px-6 flex flex-col items-center text-center gap-6" data-id="element-199">
          <div className="flex items-center gap-6 text-sm" data-id="element-200">
            <button onClick={() => setModalOpen('privacy')} className="text-charcoal/60 hover:text-gold transition-colors font-medium" data-id="element-201">
              Política de Privacidad
            </button>
            <span className="text-charcoal/20" data-id="element-202">•</span>
            <button onClick={() => setModalOpen('terms')} className="text-charcoal/60 hover:text-gold transition-colors font-medium" data-id="element-203">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-charcoal/50 text-sm max-w-2xl leading-relaxed" data-id="element-204">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar de persona a persona. Siempre consulte a su
            médico antes de realizar cambios en su dieta o rutina de salud. Este
            sitio no es parte del sitio web de Facebook o Facebook Inc. Además,
            este sitio NO está respaldado por Facebook de ninguna manera.
            FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>

          <p className="text-charcoal/40 text-sm" data-id="element-205">
            © {new Date().getFullYear()} Vitarutinas — Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-206">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModalOpen(null)} data-id="element-207">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-208">
              <div className="flex items-center justify-between p-6 md:p-8 border-b border-black/5 bg-cream/50" data-id="element-209">
                <h3 className="font-serif text-2xl font-bold text-deep-green" data-id="element-210">
                  {modalOpen === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setModalOpen(null)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white hover:bg-black/5 transition-colors text-charcoal/60 shadow-sm" data-id="element-211">
                  <XIcon className="w-5 h-5" data-id="element-212" />
                </button>
              </div>
              <div className="p-6 md:p-8 overflow-y-auto text-charcoal/80 text-base leading-relaxed whitespace-pre-line font-sans" data-id="element-213">
                {'T\xE9rminos y Condiciones \u2014 Vitarutinas\n\nAl adquirir el Protocolo Vitarutinas 7D, aceptas que se trata de un contenido de acceso digital inmediato.\n\nDebido a la naturaleza del producto (descarga electr\xF3nica), una vez entregado el acceso, no se realizan devoluciones a menos que se aplique la garant\xEDa de satisfacci\xF3n de 7 d\xEDas descrita en esta p\xE1gina.\n\nEl precio es un pago \xFAnico, sin cargos recurrentes ni suscripciones ocultas.'}
              </div>
              <div className="p-6 md:p-8 border-t border-black/5 bg-cream/50" data-id="element-214">
                <button onClick={() => setModalOpen(null)} className="w-full py-4 bg-deep-green hover:bg-deep-green/90 rounded-2xl text-white font-bold text-lg transition-colors shadow-md" data-id="element-215">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}